import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';
import { DynamicComponent } from './DynamicComponent';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  componentCode?: string;
  userPrompt?: string;
}

interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
}

const MessageBubble = ({ message, isNew }: { message: ChatMessage; isNew: boolean }) => {
  const isUser = message.role === 'user';
  const [showCode, setShowCode] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isStarring, setIsStarring] = useState(false);

  const handleFavorite = async () => {
    if (!message.componentCode || !message.userPrompt || isStarring) return;

    setIsStarring(true);
    try {
      const response = await fetch('/api/favorite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userPrompt: message.userPrompt,
          componentCode: message.componentCode,
        }),
      });

      if (response.ok) {
        setIsFavorited(true);
      }
    } catch (error) {
      console.error('Failed to favorite:', error);
    } finally {
      setIsStarring(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, x: isUser ? 15 : -15 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      transition={{
        duration: isNew ? 0.6 : 0.3,
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '16px',
      }}
    >
      <div style={{
        maxWidth: 'min(85%, 800px)',
        background: isUser
          ? 'rgba(155, 165, 163, 0.15)'
          : 'rgba(155, 165, 163, 0.05)',
        border: `1px solid ${isUser ? 'var(--color-accent)' : 'rgba(155, 165, 163, 0.15)'}`,
        borderRadius: isUser ? '12px 12px 0 12px' : '12px 12px 12px 0',
        padding: '12px 16px',
      }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text)',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}>
          {message.content}
        </p>
      </div>

      {message.componentCode && (
        <div style={{ marginTop: '12px', width: '100%', maxWidth: 'min(95%, 1000px)' }}>
          <DynamicComponent code={message.componentCode} />

          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={() => setShowCode(!showCode)}
              style={{
                padding: '4px 8px',
                fontSize: '9px',
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-text-dim)',
                background: 'transparent',
                border: '1px solid rgba(155, 165, 163, 0.2)',
                borderRadius: '4px',
                cursor: 'pointer',
                letterSpacing: '1px',
              }}
            >
              [{showCode ? 'HIDE_CODE' : 'SHOW_CODE'}]
            </button>

            <button
              onClick={handleFavorite}
              disabled={isFavorited || isStarring}
              style={{
                padding: '4px 8px',
                fontSize: '9px',
                fontFamily: 'var(--font-mono)',
                color: isFavorited ? 'var(--color-accent)' : 'var(--color-text-dim)',
                background: 'transparent',
                border: `1px solid ${isFavorited ? 'var(--color-accent)' : 'rgba(155, 165, 163, 0.2)'}`,
                borderRadius: '4px',
                cursor: isFavorited || isStarring ? 'default' : 'pointer',
                letterSpacing: '1px',
                opacity: isStarring ? 0.5 : 1,
              }}
            >
              [{isFavorited ? '★ SAVED' : isStarring ? '...' : '☆ SAVE'}]
            </button>
          </div>
          
          <AnimatePresence>
            {showCode && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  marginTop: '8px',
                  background: 'var(--color-code-bg)',
                  border: '1px solid rgba(155, 165, 163, 0.2)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid rgba(155, 165, 163, 0.1)',
                  fontSize: '9px',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-accent)',
                  letterSpacing: '1px',
                }}>
                  [COMPONENT_SOURCE]
                </div>
                <pre style={{
                  margin: 0,
                  padding: '12px',
                  fontSize: '10px',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text)',
                  lineHeight: 1.5,
                  overflow: 'auto',
                  maxHeight: '300px',
                }}>
                  <code>{message.componentCode}</code>
                </pre>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <div style={{
        fontSize: '9px',
        fontFamily: 'var(--font-mono)',
        color: 'var(--color-text-dim)',
        marginTop: '6px',
      }}>
        {message.timestamp.toLocaleTimeString()}
      </div>
    </motion.div>
  );
};

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    style={{
      display: 'flex',
      gap: '5px',
      padding: '8px 0',
      marginBottom: '16px',
    }}
  >
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: 'var(--color-accent)',
        }}
      />
    ))}
  </motion.div>
);

export function ChatPanel({
  messages,
  onSendMessage,
  isLoading = false,
  placeholder = '...',
}: ChatPanelProps) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevMessagesLength = useRef(messages.length);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const targetScroll = container.scrollHeight - container.clientHeight;
      const startScroll = container.scrollTop;
      const distance = targetScroll - startScroll;
      const duration = 800;
      let startTime: number | null = null;

      const animateScroll = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        container.scrollTop = startScroll + distance * easeOut;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, []);

  useEffect(() => {
    if (messages.length > prevMessagesLength.current) {
      setTimeout(scrollToBottom, 100);
      inputRef.current?.focus();
    }
    prevMessagesLength.current = messages.length;
  }, [messages, scrollToBottom]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const newMessageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (messages.length > 0) {
      const latestId = messages[messages.length - 1].id;
      newMessageIds.current.add(latestId);
      setTimeout(() => {
        newMessageIds.current.delete(latestId);
      }, 700);
    }
  }, [messages]);

  return (
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      height: '100%',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--color-surface)',
      border: '1px solid rgba(155, 165, 163, 0.15)',
      borderRadius: '12px',
      padding: 'clamp(8px, 3vw, 32px)',
      color: 'var(--color-text)',
      fontFamily: 'var(--font-mono)',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '8px',
          marginBottom: '16px',
          scrollBehavior: 'auto',
        }}
      >
        {messages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'var(--color-text-dim)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div style={{
              fontSize: '11px',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}>
            </div>
            <div style={{
              fontSize: '10px',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '1px',
              maxWidth: '300px',
              lineHeight: 1.6,
            }}>
            </div>
          </motion.div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isNew={newMessageIds.current.has(message.id)}
              />
            ))}
            {isLoading && <TypingIndicator />}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} style={{ flexShrink: 0 }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          borderTop: '1px solid rgba(155, 165, 163, 0.15)',
          paddingTop: '16px',
        }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '13px',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text)',
              backgroundColor: 'rgba(155, 165, 163, 0.05)',
              border: '1px solid rgba(155, 165, 163, 0.2)',
              borderRadius: '6px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-accent)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(155, 165, 163, 0.2)';
            }}
          />
          <motion.button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: '12px 24px',
              fontSize: '11px',
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              backgroundColor: inputValue.trim() && !isLoading ? 'var(--color-accent)' : 'rgba(155, 165, 163, 0.2)',
              color: inputValue.trim() && !isLoading ? 'var(--color-bg)' : 'var(--color-text-dim)',
              border: 'none',
              borderRadius: '6px',
              cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s, color 0.2s',
            }}
          >
            [SEND]
          </motion.button>
        </div>
        <div style={{
          marginTop: '8px',
          fontSize: '9px',
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-dim)',
          letterSpacing: '0.5px',
        }}>
          ENTER: SEND | EXAMPLE: "show me a weather widget for NYC"
        </div>
      </form>
    </div>
  );
}

export default ChatPanel;
